import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, ChevronDown, ChevronUp, Pencil, Trash2,
  Globe, Shield, CreditCard, LayoutGrid, Settings, Users, FileText,
  LogOut, Bell, X, Check, AlertTriangle, ToggleLeft, ToggleRight, BookOpen
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import AdminSidebar from '../components/AdminSidebar';
import LanguageToggle from '../components/LanguageToggle';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from '../components/ui/sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('cop31_ultra_token')}` } });

const CATEGORIES = [
  { id: 'all',          label_tr: 'Tümü',         label_en: 'All',          icon: BookOpen,    color: 'bg-gray-100 text-gray-700 border-gray-200' },
  { id: 'registration', label_tr: 'Kayıt & Onay', label_en: 'Registration', icon: FileText,    color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { id: 'payment',      label_tr: 'Ödeme',         label_en: 'Payment',      icon: CreditCard,  color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  { id: 'stand',        label_tr: 'Stand',          label_en: 'Stand',        icon: LayoutGrid,  color: 'bg-violet-50 text-violet-700 border-violet-200' },
  { id: 'technical',    label_tr: 'Teknik',         label_en: 'Technical',    icon: Settings,    color: 'bg-amber-50 text-amber-700 border-amber-200' },
  { id: 'privacy',      label_tr: 'Gizlilik',       label_en: 'Privacy',      icon: Shield,      color: 'bg-rose-50 text-rose-700 border-rose-200' },
  { id: 'conduct',      label_tr: 'Davranış',       label_en: 'Conduct',      icon: Users,       color: 'bg-teal-50 text-teal-700 border-teal-200' },
];

const EMPTY_RULE = { category: 'conduct', title_tr: '', title_en: '', content_tr: '', content_en: '', status: 'active' };

export default function PlatformRules() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = language === 'tr';

  const [rules, setRules]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [expanded, setExpanded] = useState(new Set());
  const [modal, setModal]       = useState(null);   // null | 'add' | 'edit'
  const [form, setForm]         = useState(EMPTY_RULE);
  const [saving, setSaving]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('cop31_ultra_token');
    if (!token) { navigate('/giris?tab=ultra'); return; }
    fetchRules();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ultra-admin/platform-rules`, getAuth());
      setRules(res.data);
    } catch (e) {
      if (e.response?.status === 401) navigate('/giris?tab=ultra');
    } finally { setLoading(false); }
  };

  /* ── computed ── */
  const catCounts = useMemo(() => {
    const m = { all: rules.length };
    CATEGORIES.slice(1).forEach(c => { m[c.id] = rules.filter(r => r.category === c.id).length; });
    return m;
  }, [rules]);

  const filtered = useMemo(() => rules.filter(r => {
    const matchCat = activeCat === 'all' || r.category === activeCat;
    const q = search.toLowerCase();
    const matchQ = !q
      || r.title_tr.toLowerCase().includes(q)
      || r.title_en.toLowerCase().includes(q)
      || r.content_tr.toLowerCase().includes(q)
      || r.content_en.toLowerCase().includes(q);
    return matchCat && matchQ;
  }), [rules, activeCat, search]);

  /* ── helpers ── */
  const catMeta = id => CATEGORIES.find(c => c.id === id) || CATEGORIES[0];
  const toggle  = id => setExpanded(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const setF    = (k, v) => setForm(p => ({ ...p, [k]: v }));

  /* ── CRUD ── */
  const openAdd = () => { setForm(EMPTY_RULE); setModal('add'); };
  const openEdit = rule => { setForm({ ...rule }); setModal('edit'); };
  const closeModal = () => { setModal(null); setSaving(false); };

  const handleSave = async () => {
    if (!form.title_tr || !form.title_en) {
      toast.error(tr ? 'TR ve EN başlık zorunludur.' : 'Both TR and EN titles are required.'); return;
    }
    setSaving(true);
    try {
      if (modal === 'add') {
        const res = await axios.post(`${API}/ultra-admin/platform-rules`, form, getAuth());
        setRules(p => [...p, res.data]);
        toast.success(tr ? 'Kural eklendi.' : 'Rule added.');
      } else {
        const res = await axios.put(`${API}/ultra-admin/platform-rules/${form.id}`, form, getAuth());
        setRules(p => p.map(r => r.id === form.id ? res.data : r));
        toast.success(tr ? 'Kural güncellendi.' : 'Rule updated.');
      }
      closeModal();
    } catch (e) {
      toast.error(e.response?.data?.detail || (tr ? 'Hata oluştu.' : 'Error occurred.'));
    } finally { setSaving(false); }
  };

  const handleToggleStatus = async rule => {
    const newStatus = rule.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await axios.put(`${API}/ultra-admin/platform-rules/${rule.id}`, { status: newStatus }, getAuth());
      setRules(p => p.map(r => r.id === rule.id ? res.data : r));
      toast.success(newStatus === 'active' ? (tr ? 'Kural aktif edildi.' : 'Rule activated.') : (tr ? 'Kural pasife alındı.' : 'Rule deactivated.'));
    } catch { toast.error(tr ? 'Durum güncellenemedi.' : 'Status update failed.'); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/ultra-admin/platform-rules/${deleteTarget.id}`, getAuth());
      setRules(p => p.filter(r => r.id !== deleteTarget.id));
      setDeleteTarget(null);
      toast.success(tr ? 'Kural silindi.' : 'Rule deleted.');
    } catch { toast.error(tr ? 'Silinemedi.' : 'Delete failed.'); }
  };

  const handleLogout = () => {
    localStorage.removeItem('cop31_ultra_token');
    localStorage.removeItem('cop31_ultra_user');
    navigate('/giris?tab=ultra');
  };

  /* ── render ── */
  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <AdminSidebar />
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Top Bar */}
        <div className="bg-white border-b border-gray-100 px-8 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{tr ? 'Platform Kuralları' : 'Platform Rules'}</h1>
            <p className="text-sm text-gray-500">{tr ? 'COP 31 platform politikaları ve kurallar yönetimi' : 'COP 31 platform policies and rules management'}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="rounded-full"><Bell className="h-5 w-5 text-gray-500" /></Button>
            <LanguageToggle variant="admin" />
            <Button variant="ghost" size="sm" onClick={handleLogout} className="rounded-full gap-2 text-gray-600">
              <LogOut className="h-4 w-4" />{tr ? 'Çıkış' : 'Logout'}
            </Button>
          </div>
        </div>

        {/* Main */}
        <div className="flex-1 overflow-y-auto p-8">
          <PageIntro {...getPageIntro('platform-rules', language)} />

          {/* Search + Add */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                data-testid="rules-search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={tr ? 'Kural başlığı veya içeriğinde ara...' : 'Search in rule title or content...'}
                className="pl-10 rounded-2xl border-gray-200 bg-white shadow-sm"
              />
            </div>
            <Button data-testid="add-rule-btn" onClick={openAdd} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl gap-2 px-5 flex-shrink-0">
              <Plus className="h-4 w-4" />{tr ? 'Yeni Kural' : 'New Rule'}
            </Button>
          </div>

          {/* Category Tabs */}
          <div className="flex gap-2 flex-wrap mb-6">
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              const active = activeCat === cat.id;
              return (
                <button
                  key={cat.id}
                  data-testid={`cat-tab-${cat.id}`}
                  onClick={() => setActiveCat(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                    active
                      ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-300 hover:text-emerald-700'
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {tr ? cat.label_tr : cat.label_en}
                  <span className={`text-xs rounded-full px-1.5 py-0.5 font-bold ${active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                    {catCounts[cat.id] || 0}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Rules List */}
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="animate-spin h-8 w-8 rounded-full border-4 border-emerald-500 border-t-transparent" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium">{search ? (tr ? 'Arama sonucu bulunamadı.' : 'No results found.') : (tr ? 'Henüz kural yok.' : 'No rules yet.')}</p>
              {!search && <Button onClick={openAdd} variant="outline" className="mt-4 rounded-2xl gap-2"><Plus className="h-4 w-4" />{tr ? 'İlk Kuralı Ekle' : 'Add First Rule'}</Button>}
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((rule, idx) => {
                const meta = catMeta(rule.category);
                const CatIcon = meta.icon;
                const isExpanded = expanded.has(rule.id);
                const isActive = rule.status === 'active';
                return (
                  <div
                    key={rule.id}
                    data-testid={`rule-card-${rule.id}`}
                    className={`bg-white rounded-2xl border transition-all duration-200 ${isActive ? 'border-gray-200 shadow-sm' : 'border-gray-100 opacity-60'}`}
                  >
                    {/* Header row */}
                    <div className="flex items-center gap-4 p-5 cursor-pointer" onClick={() => toggle(rule.id)}>
                      {/* Index */}
                      <div className="h-8 w-8 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-gray-400">{String(idx + 1).padStart(2, '0')}</span>
                      </div>

                      {/* Category Badge */}
                      <Badge className={`rounded-full border text-xs font-semibold flex-shrink-0 ${meta.color}`}>
                        <CatIcon className="h-3 w-3 mr-1" />
                        {tr ? meta.label_tr : meta.label_en}
                      </Badge>

                      {/* Title */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{tr ? rule.title_tr : rule.title_en}</p>
                        {!isExpanded && (
                          <p className="text-xs text-gray-400 truncate mt-0.5">
                            {(tr ? rule.content_tr : rule.content_en).slice(0, 90)}…
                          </p>
                        )}
                      </div>

                      {/* Status + Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                        <button
                          data-testid={`toggle-status-${rule.id}`}
                          onClick={() => handleToggleStatus(rule)}
                          title={isActive ? (tr ? 'Pasife al' : 'Deactivate') : (tr ? 'Aktif et' : 'Activate')}
                          className={`transition-colors rounded-full ${isActive ? 'text-emerald-500 hover:text-emerald-700' : 'text-gray-300 hover:text-gray-500'}`}
                        >
                          {isActive ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
                        </button>
                        <Button size="sm" variant="ghost" className="rounded-full h-8 w-8 p-0 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50"
                          onClick={() => openEdit(rule)} data-testid={`edit-rule-${rule.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-full h-8 w-8 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50"
                          onClick={() => setDeleteTarget(rule)} data-testid={`delete-rule-${rule.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                        <div className="text-gray-300">{isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 px-5 pb-5 pt-4">
                        <h4 className="font-bold text-gray-800 mb-2">{tr ? rule.title_tr : rule.title_en}</h4>
                        <p className="text-sm text-gray-600 leading-relaxed">{tr ? rule.content_tr : rule.content_en}</p>
                        {rule.updated_at && (
                          <p className="text-xs text-gray-300 mt-4">
                            {tr ? 'Son güncelleme:' : 'Last updated:'} {new Date(rule.updated_at).toLocaleDateString(tr ? 'tr-TR' : 'en-GB')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {modal === 'add' ? (tr ? 'Yeni Kural Ekle' : 'Add New Rule') : (tr ? 'Kuralı Düzenle' : 'Edit Rule')}
                </h2>
                <p className="text-sm text-gray-400">{tr ? 'TR ve EN alanlar zorunludur.' : 'TR and EN fields are required.'}</p>
              </div>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 rounded-full p-1 hover:bg-gray-100">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto p-6 space-y-5 flex-1">
              {/* Category + Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm text-gray-700">{tr ? 'Kategori' : 'Category'}</Label>
                  <select
                    value={form.category}
                    onChange={e => setF('category', e.target.value)}
                    className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    {CATEGORIES.slice(1).map(c => (
                      <option key={c.id} value={c.id}>{tr ? c.label_tr : c.label_en}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm text-gray-700">{tr ? 'Durum' : 'Status'}</Label>
                  <select
                    value={form.status}
                    onChange={e => setF('status', e.target.value)}
                    className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="active">{tr ? 'Aktif' : 'Active'}</option>
                    <option value="inactive">{tr ? 'Pasif' : 'Inactive'}</option>
                  </select>
                </div>
              </div>

              {/* TR Section */}
              <div className="bg-blue-50 rounded-2xl p-4 space-y-3">
                <div className="flex items-center gap-2 text-blue-700 font-semibold text-sm">
                  <span>🇹🇷</span> Türkçe
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-blue-600">Başlık (TR)</Label>
                  <Input value={form.title_tr} onChange={e => setF('title_tr', e.target.value)}
                    placeholder="Kural başlığı..." className="rounded-xl bg-white border-blue-200 focus:ring-blue-400" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-blue-600">İçerik (TR)</Label>
                  <Textarea value={form.content_tr} onChange={e => setF('content_tr', e.target.value)}
                    placeholder="Kural içeriği..." rows={4}
                    className="rounded-xl bg-white border-blue-200 focus:ring-blue-400 resize-none" />
                </div>
              </div>

              {/* EN Section */}
              <div className="bg-emerald-50 rounded-2xl p-4 space-y-3">
                <div className="flex items-center gap-2 text-emerald-700 font-semibold text-sm">
                  <span>🇬🇧</span> English
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-emerald-600">Title (EN)</Label>
                  <Input value={form.title_en} onChange={e => setF('title_en', e.target.value)}
                    placeholder="Rule title..." className="rounded-xl bg-white border-emerald-200 focus:ring-emerald-400" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-emerald-600">Content (EN)</Label>
                  <Textarea value={form.content_en} onChange={e => setF('content_en', e.target.value)}
                    placeholder="Rule content..." rows={4}
                    className="rounded-xl bg-white border-emerald-200 focus:ring-emerald-400 resize-none" />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-6 border-t border-gray-100 flex-shrink-0">
              <Button variant="outline" className="flex-1 rounded-2xl" onClick={closeModal}>{tr ? 'İptal' : 'Cancel'}</Button>
              <Button
                data-testid="save-rule-btn"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl gap-2"
              >
                {saving
                  ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  : <><Check className="h-4 w-4" />{modal === 'add' ? (tr ? 'Ekle' : 'Add') : (tr ? 'Kaydet' : 'Save')}</>}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm ── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">{tr ? 'Kuralı Sil' : 'Delete Rule'}</h3>
                <p className="text-sm text-gray-500 line-clamp-2">{tr ? deleteTarget.title_tr : deleteTarget.title_en}</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 bg-red-50 rounded-xl p-3 mb-5">
              {tr ? 'Bu işlem geri alınamaz. Kural kalıcı olarak silinecek.' : 'This action cannot be undone. The rule will be permanently deleted.'}
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-2xl" onClick={() => setDeleteTarget(null)}>{tr ? 'İptal' : 'Cancel'}</Button>
              <Button onClick={handleDelete} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-2xl gap-2">
                <Trash2 className="h-4 w-4" />{tr ? 'Sil' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
